import * as z from 'zod'
import { Role } from '~/shared/api'
import { ApiModel, ToManyRelation } from '~/shared/api/core'
import { Option } from '~/shared/config/constants'
import { buildI18nMessage } from '~/shared/lib/i18n'
import {
  dateSchema,
  enumOptionSchema,
  stringRequiredScheme,
  uuidOptionsSchema,
} from '~/shared/lib/schemas'
import { isString } from '~/shared/lib/utils'
import { LocaleEnum, TimezoneCode } from '../config/enums'

const attributeSchema = z.object({
  email: stringRequiredScheme.email(
    buildI18nMessage('validate.invalid email address'),
  ),
  name: z.string().trim().min(1, buildI18nMessage('validate.required field')),
  timezone: enumOptionSchema(TimezoneCode),
  locale: enumOptionSchema(LocaleEnum),
})
const relationsSchema = z.object({
  rolesIds: uuidOptionsSchema.min(
    1,
    buildI18nMessage('validate.required field'),
  ),
})
const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type EmployeeAttributes = z.infer<typeof attributeSchema>

export class Employee extends ApiModel<typeof schema, EmployeeAttributes> {
  static jsonApiType = 'users'
  static schema = schema

  getEmail(): string {
    return this.getAttribute('email')
  }
  getName(): string {
    return this.getAttribute('name')
  }
  getTimezone(): TimezoneCode {
    return this.getAttribute('timezone')
  }
  getLocale(): LocaleEnum {
    return this.getAttribute('locale')
  }

  roles(): ToManyRelation<Role, this> {
    return this.hasMany(Role)
  }
  getRoles(): Role[] {
    return this.getRelation('roles') ?? []
  }
  setRoles(ids: UniqueId[] | Option[]) {
    let roles: Role[] = []
    ids.map((roleId) => {
      const role = new Role()
      if (isString(roleId)) {
        role.setApiId(roleId)
        roles = roles.concat(role)
      }
    })
    this.setRelation('roles', roles)
  }

  getOption(): { id: UniqueId; label: string } {
    return {
      id: this.getApiId() as UniqueId,
      label: this.getName(),
    }
  }
  static async fetchOptions(search: string) {
    const response = await Employee.where('name', search).get(1)
    return response.getData().map((o) => o.getOption())
  }
}
